import { useSyncExternalStore } from 'react';

import type { ChartSpec, DataQaMessage, ExecutedQuery } from '../../services/api/dataQa';

export type ChatEntry = DataQaMessage & {
  queries?: ExecutedQuery[];
  charts?: ChartSpec[];
  model?: string;
};

export type Thread = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  entries: ChatEntry[];
};

export type ThreadState = { activeId: string | null; threads: Thread[] };

export const MAX_THREADS = 30;
const TITLE_LENGTH = 80;
const KEY_PREFIX = 'data-qa:threads:';

const EMPTY: ThreadState = { activeId: null, threads: [] };

const storageKey = (userId: string): string => `${KEY_PREFIX}${userId}`;

const listeners = new Set<() => void>();
// useSyncExternalStore kräver samma referens så länge inget ändrats, men JSON.parse ger
// en ny varje gång. Cachen nycklas på råsträngen, så en skrivning från en annan flik syns.
const cache = new Map<string, { raw: string | null; state: ThreadState }>();

const isThread = (value: unknown): value is Thread => {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.id === 'string' &&
    typeof t.title === 'string' &&
    typeof t.createdAt === 'string' &&
    typeof t.updatedAt === 'string' &&
    Array.isArray(t.entries)
  );
};

const parse = (raw: string | null): ThreadState => {
  if (!raw) return EMPTY;
  try {
    const value = JSON.parse(raw) as Partial<ThreadState> | null;
    const threads = Array.isArray(value?.threads) ? value.threads.filter(isThread) : [];
    const activeId =
      typeof value?.activeId === 'string' && threads.some((t) => t.id === value.activeId)
        ? value.activeId
        : null;
    return { activeId, threads };
  } catch {
    return EMPTY;
  }
};

const readRaw = (userId: string): string | null => {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
};

const read = (userId: string): ThreadState => {
  const raw = readRaw(userId);
  const cached = cache.get(userId);
  if (cached && cached.raw === raw) return cached.state;
  const state = parse(raw);
  cache.set(userId, { raw, state });
  return state;
};

const write = (userId: string, state: ThreadState): void => {
  const raw = JSON.stringify(state);
  try {
    localStorage.setItem(storageKey(userId), raw);
  } catch {
    // Full eller blockerad lagring: tråden lever vidare i cachen tills sidan laddas om.
  }
  cache.set(userId, { raw: readRaw(userId) ?? raw, state });
  for (const listener of listeners) listener();
};

// Resultatraderna kan vara 500 per fråga och fyller lagringen; SQL, syfte och radantal räcker
// för att se hur det räknades. Raderna bakom ett diagram behålls (högst 120, samma tak som
// backend ritar), annars försvinner diagrammet ur en sparad tråd.
const stripRows = (entries: ChatEntry[]): ChatEntry[] =>
  entries.map((entry) => {
    if (!entry.queries) return entry;
    const charted = new Set((entry.charts ?? []).map((c) => c.queryIndex));
    return {
      ...entry,
      queries: entry.queries.map((q, index) =>
        charted.has(index) ? { ...q, rows: q.rows.slice(0, 120) } : { ...q, rows: [] },
      ),
    };
  });

const titleOf = (entries: ChatEntry[]): string => {
  const first = entries.find((e) => e.role === 'user')?.content.trim() ?? '';
  return first.length > TITLE_LENGTH ? `${first.slice(0, TITLE_LENGTH - 1)}…` : first;
};

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const startThread = (userId: string): void => {
  const state = read(userId);
  if (state.activeId !== null) write(userId, { ...state, activeId: null });
};

export const appendEntries = (
  userId: string,
  threadId: string | null,
  entries: ChatEntry[],
): string => {
  const state = read(userId);
  const now = new Date().toISOString();
  const existing = threadId ? state.threads.find((t) => t.id === threadId) : undefined;
  const thread: Thread = existing
    ? { ...existing, entries: [...existing.entries, ...stripRows(entries)], updatedAt: now }
    : {
        id: newId(),
        title: titleOf(entries),
        createdAt: now,
        updatedAt: now,
        entries: stripRows(entries),
      };
  const others = state.threads.filter((t) => t.id !== thread.id);
  write(userId, {
    activeId: thread.id,
    threads: [thread, ...others].slice(0, MAX_THREADS),
  });
  return thread.id;
};

export const openThread = (userId: string, threadId: string): void => {
  const state = read(userId);
  if (state.threads.some((t) => t.id === threadId)) write(userId, { ...state, activeId: threadId });
};

export const deleteThread = (userId: string, threadId: string): void => {
  const state = read(userId);
  write(userId, {
    activeId: state.activeId === threadId ? null : state.activeId,
    threads: state.threads.filter((t) => t.id !== threadId),
  });
};

const subscribe = (callback: () => void): (() => void) => {
  listeners.add(callback);
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(KEY_PREFIX)) callback();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(callback);
    window.removeEventListener('storage', onStorage);
  };
};

export const useThreads = (userId: string): ThreadState =>
  useSyncExternalStore(
    subscribe,
    () => read(userId),
    () => EMPTY,
  );
